import { Component, ReactNode, ErrorInfo } from "react";
import { Page, Card, Text, BlockStack, Banner, Button } from "@shopify/polaris";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Also log to console for browser-side debugging
    // eslint-disable-next-line no-console
    console.error("[ShipPromise ErrorBoundary]", error, errorInfo);
    this.setState({ error, errorInfo });
  }

  reset = (): void => {
    this.setState({ error: null, errorInfo: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <Page title="Something went wrong">
          <Banner tone="critical" title="This page crashed">
            <p>
              An unexpected error occurred while rendering this page. Please copy the details
              below and share them with support, then click Reload.
            </p>
          </Banner>
          <div style={{ marginTop: "1rem" }}>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">
                  Error
                </Text>
                <Text as="p" variant="bodyMd">
                  {this.state.error.name}: {this.state.error.message}
                </Text>
                {this.state.error.stack && (
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      background: "#f6f6f7",
                      padding: "1rem",
                      borderRadius: "4px",
                      fontSize: "12px",
                      maxHeight: "300px",
                      overflow: "auto",
                    }}
                  >
                    {this.state.error.stack}
                  </pre>
                )}
                {this.state.errorInfo?.componentStack && (
                  <>
                    <Text as="h3" variant="headingMd">
                      Component stack
                    </Text>
                    <pre
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        background: "#f6f6f7",
                        padding: "1rem",
                        borderRadius: "4px",
                        fontSize: "12px",
                        maxHeight: "300px",
                        overflow: "auto",
                      }}
                    >
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </>
                )}
                <Button onClick={this.reset}>Retry</Button>
              </BlockStack>
            </Card>
          </div>
        </Page>
      );
    }
    return this.props.children;
  }
}
